import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { adminMessagesService, adminClubInterestService, adminActivityService } from '../../../services/adminContentService';
import { playerProfileService } from '../../../services/playerMasterService';

const AdminContentManager = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('messages');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [clubInterests, setClubInterests] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [players, setPlayers] = useState([]);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [showInterestForm, setShowInterestForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editingInterest, setEditingInterest] = useState(null);

  const [messageForm, setMessageForm] = useState({
    targetPlayerId: '',
    targetUserId: '',
    isGlobal: false,
    messageType: 'info',
    title: '',
    description: '',
    icon: 'MessageSquare',
    iconColor: 'primary',
    isActive: true,
    priority: 0
  });

  const [interestForm, setInterestForm] = useState({
    playerId: '',
    clubId: '',
    interestLevel: 'monitoring',
    notes: '',
    priority: 'normal',
    isActive: true
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'messages') {
        const [messagesRes, playersRes] = await Promise.all([
          adminMessagesService?.getAll(),
          playerProfileService?.getAll()
        ]);
        setMessages(messagesRes?.data || []);
        setPlayers(playersRes?.data || []);
      } else if (activeTab === 'club-interests') {
        const [interestsRes, playersRes] = await Promise.all([
          adminClubInterestService?.getAll(),
          playerProfileService?.getAll()
        ]);
        setClubInterests(interestsRes?.data || []);
        setPlayers(playersRes?.data || []);
      } else if (activeTab === 'activity-log') {
        const logRes = await adminActivityService?.getLog({ limit: 100 });
        setActivityLog(logRes?.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMessage = async () => {
    try {
      await adminMessagesService?.create(messageForm);
      setShowMessageForm(false);
      resetMessageForm();
      loadData();
    } catch (error) {
      console.error('Error creating message:', error);
      alert('Failed to create message: ' + error?.message);
    }
  };

  const handleUpdateMessage = async () => {
    try {
      await adminMessagesService?.update(editingMessage?.id, messageForm);
      setEditingMessage(null);
      resetMessageForm();
      loadData();
    } catch (error) {
      console.error('Error updating message:', error);
      alert('Failed to update message: ' + error?.message);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await adminMessagesService?.delete(id);
      loadData();
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message: ' + error?.message);
    }
  };

  const handleCreateInterest = async () => {
    try {
      await adminClubInterestService?.create(interestForm);
      setShowInterestForm(false);
      resetInterestForm();
      loadData();
    } catch (error) {
      console.error('Error creating interest:', error);
      alert('Failed to create club interest: ' + error?.message);
    }
  };

  const handleUpdateInterest = async () => {
    try {
      await adminClubInterestService?.update(editingInterest?.id, interestForm);
      setEditingInterest(null);
      resetInterestForm();
      loadData();
    } catch (error) {
      console.error('Error updating interest:', error);
      alert('Failed to update club interest: ' + error?.message);
    }
  };

  const handleDeleteInterest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this club interest?')) return;
    try {
      await adminClubInterestService?.delete(id);
      loadData();
    } catch (error) {
      console.error('Error deleting interest:', error);
      alert('Failed to delete club interest: ' + error?.message);
    }
  };

  const resetMessageForm = () => {
    setMessageForm({
      targetPlayerId: '',
      targetUserId: '',
      isGlobal: false,
      messageType: 'info',
      title: '',
      description: '',
      icon: 'MessageSquare',
      iconColor: 'primary',
      isActive: true,
      priority: 0
    });
    setShowMessageForm(false);
    setEditingMessage(null);
  };

  const resetInterestForm = () => {
    setInterestForm({
      playerId: '',
      clubId: '',
      interestLevel: 'monitoring',
      notes: '',
      priority: 'normal',
      isActive: true
    });
    setShowInterestForm(false);
    setEditingInterest(null);
  };

  const editMessage = (message) => {
    setMessageForm({
      targetPlayerId: message?.targetPlayerId || '',
      targetUserId: message?.targetUserId || '',
      isGlobal: message?.isGlobal || false,
      messageType: message?.messageType || 'info',
      title: message?.title || '',
      description: message?.description || '',
      icon: message?.icon || 'MessageSquare',
      iconColor: message?.iconColor || 'primary',
      isActive: message?.isActive !== undefined ? message?.isActive : true,
      priority: message?.priority || 0
    });
    setEditingMessage(message);
    setShowMessageForm(true);
  };

  const editInterest = (interest) => {
    setInterestForm({
      playerId: interest?.playerId || '',
      clubId: interest?.clubId || '',
      interestLevel: interest?.interestLevel || 'monitoring',
      notes: interest?.notes || '',
      priority: interest?.priority || 'normal',
      isActive: interest?.isActive !== undefined ? interest?.isActive : true
    });
    setEditingInterest(interest);
    setShowInterestForm(true);
  };

  const tabs = [
    { id: 'messages', label: 'Player Messages', icon: 'MessageSquare' },
    { id: 'club-interests', label: 'Club Interactions', icon: 'Heart' },
    { id: 'activity-log', label: 'Activity Log', icon: 'Activity' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground font-headline">Admin Content Manager</h2>
            <p className="text-sm text-muted-foreground mt-1">Control player dashboard messages, club interactions, and platform content</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border px-6">
          <div className="flex gap-4">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary font-semibold' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Messages Tab */}
              {activeTab === 'messages' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-foreground">Player Dashboard Messages</h3>
                    <Button onClick={() => setShowMessageForm(true)} iconName="Plus" iconPosition="left">
                      Create Message
                    </Button>
                  </div>

                  {(showMessageForm || editingMessage) && (
                    <div className="bg-muted rounded-lg p-6 mb-6">
                      <h4 className="text-lg font-semibold text-foreground mb-4">
                        {editingMessage ? 'Edit Message' : 'Create New Message'}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Title"
                          value={messageForm?.title}
                          onChange={(e) => setMessageForm({ ...messageForm, title: e?.target?.value })}
                          placeholder="Message title"
                        />
                        <Select
                          label="Message Type"
                          value={messageForm?.messageType}
                          onChange={(value) => setMessageForm({ ...messageForm, messageType: value })}
                          options={[
                            { value: 'info', label: 'Info' },
                            { value: 'success', label: 'Success' },
                            { value: 'warning', label: 'Warning' },
                            { value: 'alert', label: 'Alert' },
                            { value: 'club_interest', label: 'Club Interest' },
                            { value: 'scout_feedback', label: 'Scout Feedback' },
                            { value: 'training', label: 'Training' },
                            { value: 'document', label: 'Document' },
                            { value: 'opportunity', label: 'Opportunity' }
                          ]}
                        />
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                          <textarea
                            value={messageForm?.description}
                            onChange={(e) => setMessageForm({ ...messageForm, description: e?.target?.value })}
                            placeholder="Message description"
                            rows={3}
                            className="w-full px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                        </div>
                        <Select
                          label="Target Player"
                          value={messageForm?.targetPlayerId}
                          onChange={(value) => setMessageForm({ ...messageForm, targetPlayerId: value, isGlobal: false })}
                          options={[
                            { value: '', label: 'None (Global or User-specific)' },
                            ...players?.map(p => ({ value: p?.id, label: p?.fullName }))
                          ]}
                        />
                        <Select
                          label="Icon"
                          value={messageForm?.icon}
                          onChange={(value) => setMessageForm({ ...messageForm, icon: value })}
                          options={[
                            { value: 'MessageSquare', label: 'Message' },
                            { value: 'Heart', label: 'Heart' },
                            { value: 'Star', label: 'Star' },
                            { value: 'Award', label: 'Award' },
                            { value: 'TrendingUp', label: 'Trending Up' },
                            { value: 'AlertCircle', label: 'Alert' },
                            { value: 'CheckCircle', label: 'Check' },
                            { value: 'Info', label: 'Info' }
                          ]}
                        />
                        <Select
                          label="Icon Color"
                          value={messageForm?.iconColor}
                          onChange={(value) => setMessageForm({ ...messageForm, iconColor: value })}
                          options={[
                            { value: 'primary', label: 'Primary' },
                            { value: 'success', label: 'Success' },
                            { value: 'warning', label: 'Warning' },
                            { value: 'error', label: 'Error' },
                            { value: 'accent', label: 'Accent' }
                          ]}
                        />
                        <Input
                          label="Priority (0-10)"
                          type="number"
                          value={messageForm?.priority}
                          onChange={(e) => setMessageForm({ ...messageForm, priority: parseInt(e?.target?.value) || 0 })}
                        />
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={messageForm?.isGlobal}
                              onChange={(e) => setMessageForm({ ...messageForm, isGlobal: e?.target?.checked, targetPlayerId: '' })}
                              className="w-4 h-4"
                            />
                            <span className="text-sm text-foreground">Global Message</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={messageForm?.isActive}
                              onChange={(e) => setMessageForm({ ...messageForm, isActive: e?.target?.checked })}
                              className="w-4 h-4"
                            />
                            <span className="text-sm text-foreground">Active</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <Button onClick={editingMessage ? handleUpdateMessage : handleCreateMessage}>
                          {editingMessage ? 'Update Message' : 'Create Message'}
                        </Button>
                        <Button variant="outline" onClick={resetMessageForm}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    {messages?.map((message) => (
                      <div key={message?.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`w-10 h-10 rounded-lg bg-${message?.iconColor}/10 text-${message?.iconColor} flex items-center justify-center flex-shrink-0`}>
                              <Icon name={message?.icon} size={20} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-foreground">{message?.title}</h4>
                                <span className={`px-2 py-0.5 text-xs rounded ${message?.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                                  {message?.isActive ? 'Active' : 'Inactive'}
                                </span>
                                {message?.isGlobal && (
                                  <span className="px-2 py-0.5 text-xs rounded bg-primary/10 text-primary">Global</span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">{message?.description}</p>
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <span>Type: {message?.messageType}</span>
                                <span>Priority: {message?.priority}</span>
                                {message?.targetPlayer && <span>Player: {message?.targetPlayer?.full_name}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => editMessage(message)} className="text-primary hover:text-primary/80 transition-colors">
                              <Icon name="Edit" size={18} />
                            </button>
                            <button onClick={() => handleDeleteMessage(message?.id)} className="text-error hover:text-error/80 transition-colors">
                              <Icon name="Trash2" size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {messages?.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Icon name="MessageSquare" size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No messages created yet</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Club Interests Tab */}
              {activeTab === 'club-interests' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-foreground">Club Interest Management</h3>
                    <Button onClick={() => setShowInterestForm(true)} iconName="Plus" iconPosition="left">
                      Add Club Interest
                    </Button>
                  </div>

                  {(showInterestForm || editingInterest) && (
                    <div className="bg-muted rounded-lg p-6 mb-6">
                      <h4 className="text-lg font-semibold text-foreground mb-4">
                        {editingInterest ? 'Edit Club Interest' : 'Add New Club Interest'}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select
                          label="Player"
                          value={interestForm?.playerId}
                          onChange={(value) => setInterestForm({ ...interestForm, playerId: value })}
                          options={[
                            { value: '', label: 'Select Player' },
                            ...players?.map(p => ({ value: p?.id, label: p?.fullName }))
                          ]}
                        />
                        <Select
                          label="Interest Level"
                          value={interestForm?.interestLevel}
                          onChange={(value) => setInterestForm({ ...interestForm, interestLevel: value })}
                          options={[
                            { value: 'monitoring', label: 'Monitoring' },
                            { value: 'interested', label: 'Interested' },
                            { value: 'very_interested', label: 'Very Interested' },
                            { value: 'negotiating', label: 'Negotiating' }
                          ]}
                        />
                        <Select
                          label="Priority"
                          value={interestForm?.priority}
                          onChange={(value) => setInterestForm({ ...interestForm, priority: value })}
                          options={[
                            { value: 'low', label: 'Low' },
                            { value: 'normal', label: 'Normal' },
                            { value: 'high', label: 'High' },
                            { value: 'urgent', label: 'Urgent' }
                          ]}
                        />
                        <div className="flex items-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={interestForm?.isActive}
                              onChange={(e) => setInterestForm({ ...interestForm, isActive: e?.target?.checked })}
                              className="w-4 h-4"
                            />
                            <span className="text-sm text-foreground">Active</span>
                          </label>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
                          <textarea
                            value={interestForm?.notes}
                            onChange={(e) => setInterestForm({ ...interestForm, notes: e?.target?.value })}
                            placeholder="Internal notes about this interest"
                            rows={3}
                            className="w-full px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <Button onClick={editingInterest ? handleUpdateInterest : handleCreateInterest}>
                          {editingInterest ? 'Update Interest' : 'Add Interest'}
                        </Button>
                        <Button variant="outline" onClick={resetInterestForm}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    {clubInterests?.map((interest) => (
                      <div key={interest?.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-foreground">{interest?.player?.full_name}</h4>
                              <span className={`px-2 py-0.5 text-xs rounded ${
                                interest?.interestLevel === 'negotiating' ? 'bg-error/10 text-error' :
                                interest?.interestLevel === 'very_interested' ? 'bg-warning/10 text-warning' :
                                interest?.interestLevel === 'interested'? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground'
                              }`}>
                                {interest?.interestLevel?.replace('_', ' ')}
                              </span>
                              <span className={`px-2 py-0.5 text-xs rounded ${interest?.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                                {interest?.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <div className="text-sm text-muted-foreground space-y-1">
                              <p>Club: {interest?.club?.full_name || interest?.club?.organization || 'N/A'}</p>
                              <p>Priority: {interest?.priority}</p>
                              {interest?.notes && <p className="italic">Notes: {interest?.notes}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => editInterest(interest)} className="text-primary hover:text-primary/80 transition-colors">
                              <Icon name="Edit" size={18} />
                            </button>
                            <button onClick={() => handleDeleteInterest(interest?.id)} className="text-error hover:text-error/80 transition-colors">
                              <Icon name="Trash2" size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {clubInterests?.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Icon name="Heart" size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No club interests recorded yet</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Activity Log Tab */}
              {activeTab === 'activity-log' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground mb-6">Admin Activity Log</h3>
                  <div className="space-y-2">
                    {activityLog?.map((log) => (
                      <div key={log?.id} className="bg-card border border-border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-2 py-0.5 text-xs rounded bg-primary/10 text-primary font-medium">
                                {log?.actionType?.replace('_', ' ')}
                              </span>
                              <span className="text-xs text-muted-foreground">{log?.adminEmail}</span>
                            </div>
                            <p className="text-sm text-foreground mb-1">{log?.actionDescription}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(log?.createdAt)?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {activityLog?.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Icon name="Activity" size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No activity logged yet</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminContentManager;